// The manual router (BRIEF 6.1). Call links are `/<handle>` (a personal
// room) and `/j/<code>` (a one-off link); the bare domain is ROOT_HANDLE's
// room. There is no account home: `/home` and `/settings` send a visitor to the
// bare domain (Adam, 2026-10-05: "the only surface of meet.am will be that
// particular domain"), and the call list lives in Seamless Calls. The account pages sit on
// reserved words no handle can take (packages/core RESERVED_HANDLES). No
// router library: there are only a handful of shapes to match.

import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { showsTrivia } from "./components/WaitingTrivia";
import { ACCENT_HUES, RESERVED_HANDLES, ROOT_HANDLE, isAccentHue, isAppearance, type AccountUser, type RoomInfo } from "@breeze/core";
import { api, ApiError } from "./lib/api";
import { navigate, usePathname } from "./lib/nav";
import { getKeyHandle, getOrCreateDeviceId, getRememberedName, getReturnUrl, getUserKey, ownRoomPath, setRememberedName } from "./lib/storage";
import { PreJoinScreen } from "./screens/PreJoinScreen";
import { CallScreen } from "./screens/CallScreen";
import { AuthScreen } from "./screens/AuthScreen";
import { RecordingScreen } from "./screens/RecordingScreen";
import { HistoryScreen } from "./screens/HistoryScreen";
import { ListenEmbed, ListenScreen } from "./screens/ListenScreen";
import { RecordScreen } from "./screens/RecordScreen";
import { PrepScreen } from "./screens/PrepScreen";
import { PracticeScreen } from "./screens/PracticeScreen";
import { NothingHere } from "./components/NothingHere";
import { LegalScreen } from "./screens/LegalScreen";
import { applyAppearance, getStoredAppearance, setStoredAppearance } from "./appearance";
import { clearStoredAccent, getStoredAccent, hasHouseAccent, hasStoredAccent, setStoredAccent } from "./settingsStore";
import type { RouteMatch } from "./types";
import { watchForNewDeploy } from "./lib/freshness";

type Route =
  | RouteMatch
  | { readonly kind: "home" }
  | { readonly kind: "signin" }
  | { readonly kind: "signup" }
  | { readonly kind: "settings" }
  | { readonly kind: "listen" }
  | { readonly kind: "record" }
  | { readonly kind: "prep"; readonly slug: string }
  | { readonly kind: "practice"; readonly slug: string }
  | { readonly kind: "recording"; readonly id: string }
  | { readonly kind: "history"; readonly id: string }
  | { readonly kind: "privacy" }
  | { readonly kind: "terms" };

function parseRoute(pathname: string): Route {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return { kind: "personal", slug: ROOT_HANDLE };
  if (parts.length === 2 && parts[0] === "j" && parts[1]) return { kind: "once", slug: parts[1] };
  if (parts.length === 2 && parts[0] === "recordings" && parts[1]) return { kind: "recording", id: parts[1] };
  if (parts.length === 2 && parts[0] === "history" && parts[1]) return { kind: "history", id: parts[1] };
  if (parts.length === 2 && (parts[0] === "prep" || parts[0] === "practice") && parts[1]) {
    let slug = parts[1];
    try {
      slug = decodeURIComponent(slug);
    } catch {
      // A malformed escape: the raw segment is looked up and 404s.
    }
    return { kind: parts[0], slug };
  }
  if (parts.length === 1 && parts[0]) {
    const word = parts[0].toLowerCase();
    if (word === "home") return { kind: "home" };
    if (word === "signin" || word === "login") return { kind: "signin" };
    if (word === "signup") return { kind: "signup" };
    if (word === "settings") return { kind: "settings" };
    if (word === "listen") return { kind: "listen" };
    if (word === "record") return { kind: "record" };
    if (word === "privacy") return { kind: "privacy" };
    if (word === "terms") return { kind: "terms" };
    if (!RESERVED_HANDLES.has(word)) return { kind: "personal", slug: parts[0] };
  }
  return { kind: "unknown" };
}

/** Pages a reload costs nothing on: no call, no preview, no editor. */
const RELOADABLE: ReadonlySet<string> = new Set(["recording", "history", "signin", "signup", "unknown", "privacy", "terms"]);

function applyAccent(user: AccountUser): void {
  const hue = user.settings.accent;
  if (hue && isAccentHue(hue)) {
    setStoredAccent(hue);
    document.documentElement.style.setProperty("--accent", ACCENT_HUES[hue]);
  } else if (hasHouseAccent()) {
    // The account picked no color, so a color this device remembers from an
    // earlier sign-in is stale and the page's house accent shows (QUEUE row
    // 446: an old "blue" kept the Join button off Seamless's navy).
    clearStoredAccent();
    document.documentElement.style.removeProperty("--accent");
  }

  const appearance = user.settings.appearance;
  if (isAppearance(appearance)) {
    setStoredAppearance(appearance);
    applyAppearance(appearance);
  }
}

export function App({ joinNow = false, moveNow = false }: { readonly joinNow?: boolean; readonly moveNow?: boolean }): JSX.Element {
  const pathname = usePathname();
  const route = useMemo(() => parseRoute(pathname), [pathname]);
  // An instant call (`?go=1`) skips the join screen with the remembered name.
  const [joinedName, setJoinedName] = useState<string | null>(joinNow ? getRememberedName().trim() || "Guest" : null);
  // Whether the join takes this account's seat over from another device, and
  // a count that remounts the call screen for "Move here".
  const [joinMove, setJoinMove] = useState<{ readonly move: boolean; readonly n: number }>({ move: joinNow && moveNow, n: 0 });
  /** The owner pressed Record before joining this call. */
  const [joinRecord, setJoinRecord] = useState(false);
  const [ownerName, setOwnerName] = useState<string | null>(null);
  // This account is in this call right now on another device.
  const [inCallElsewhere, setInCallElsewhere] = useState(false);
  // undefined: not asked yet. null: signed out.
  const [me, setMe] = useState<AccountUser | null | undefined>(getUserKey() ? undefined : null);

  // A new deploy reloads an idle tab (lib/freshness.ts): only on pages with
  // no call, no camera preview, and nothing he edits.
  const reloadable = useRef(false);
  reloadable.current = joinedName === null && RELOADABLE.has(route.kind);
  useEffect(() => watchForNewDeploy(() => reloadable.current), []);

  // Paint the device's chosen accent once, before anything else relies on it.
  // A device that chose none keeps the house accent the page came with.
  useEffect(() => {
    if (hasStoredAccent() || !hasHouseAccent()) document.documentElement.style.setProperty("--accent", ACCENT_HUES[getStoredAccent()]);
    applyAppearance(getStoredAppearance());
  }, []);

  const adoptUser = useCallback((user: AccountUser) => {
    setMe(user);
    applyAccent(user);
    if (!getRememberedName()) setRememberedName(user.displayName);
  }, []);

  // Who this browser is signed in as. A failure other than a 401 keeps the
  // key and shows the pages that do not need the account.
  useEffect(() => {
    if (!getUserKey()) return;
    const controller = new AbortController();
    api<AccountUser>("/api/me", { signal: controller.signal })
      .then(adoptUser)
      .catch((err: unknown) => {
        if ((err as Error).name === "AbortError") return;
        if (err instanceof ApiError && err.status === 401) setMe(null);
      });
    return () => controller.abort();
  }, [adoptUser]);

  // Leaving a call page resets the pre-join state for the next one. Only on
  // a change of page: on first load it would undo an instant call's join.
  const shownPath = useRef(pathname);
  useEffect(() => {
    if (shownPath.current === pathname) return;
    shownPath.current = pathname;
    setJoinedName(null);
    setOwnerName(null);
    setInCallElsewhere(false);
    setJoinMove({ move: false, n: 0 });
  }, [pathname]);

  // Whose room this is, for the heading and the waiting line. Off the join
  // path: the page is usable before this answers, and a failure just keeps
  // the plain heading.
  // Signed in, it also says whether this account is already in the call on
  // another device, read again whenever the page comes back into view.
  const onCallPage = route.kind === "personal" || route.kind === "once";
  const waitingToJoin = joinedName === null;
  useEffect(() => {
    if (!onCallPage || !waitingToJoin) return;
    // `stopped` marks the page as joined so a late answer no longer offers
    // Move; the request itself runs to the end.
    const stopped = new AbortController();
    const controller = { signal: stopped.signal };
    const key = getUserKey();
    const path = window.location.pathname;
    const load = (): void => {
      const query = key ? `?deviceId=${encodeURIComponent(getOrCreateDeviceId())}` : "";
      fetch(`/api/rooms/${route.kind}/${encodeURIComponent(route.slug)}/info${query}`, {
        headers: key ? { authorization: `Bearer ${key}` } : {},
      })
        .then((res) => (res.ok ? (res.json() as Promise<RoomInfo>) : null))
        .then((info) => {
          // A late answer for a page since left is dropped.
          if (window.location.pathname !== path) return;
          if (route.kind === "personal") setOwnerName(info?.ownerName ?? null);
          if (!controller.signal.aborted) setInCallElsewhere(!!info?.selfPresent && info.selfHere === false);
        })
        .catch(() => {});
    };
    load();
    const onVisible = (): void => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      // Not aborted: a guest who taps Join before this answers still needs
      // the owner's name for "Waiting for <name> to let you in".
      stopped.abort();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [route, onCallPage, waitingToJoin]);

  // Pages that need an account send a signed-out visitor to sign in, and
  // sign-in pages send a signed-in one to their own room.
  const needsAccount = route.kind === "recording" || route.kind === "history" || route.kind === "listen" || route.kind === "record" || route.kind === "prep" || route.kind === "practice";
  const signedOut = me === null;
  useEffect(() => {
    if (route.kind === "home" || route.kind === "settings") navigate(ownRoomPath(), { replace: true });
    if (needsAccount && signedOut) navigate("/signin", { replace: true });
    if ((route.kind === "signin" || route.kind === "signup") && me) navigate(ownRoomPath(), { replace: true });
  }, [me, needsAccount, route.kind, signedOut]);

  switch (route.kind) {
    case "privacy":
    case "terms":
      return <LegalScreen page={route.kind} />;
    case "signin":
    case "signup":
      return <AuthScreen mode={route.kind} onSignedIn={adoptUser} />;
    case "home":
    case "settings":
      // Old addresses: the effect above has already sent the visitor to the bare domain.
      return <Loading />;
    case "recording":
      return me ? <RecordingScreen id={route.id} /> : <Loading />;
    case "history":
      return me ? <HistoryScreen id={route.id} /> : <Loading />;
    case "listen":
      // The operator's own tool. Anyone else sees what any unknown address shows.
      if (me && me.handle.toLowerCase() !== ROOT_HANDLE) return <NothingHere />;
      if (!me) return <Loading />;
      return new URLSearchParams(window.location.search).get("embed") === "1" ? <ListenEmbed me={me} /> : <ListenScreen me={me} />;
    case "record":
      // The operator's own tool, like Listen.
      if (me && me.handle.toLowerCase() !== ROOT_HANDLE) return <NothingHere />;
      if (!me) return <Loading />;
      return <RecordScreen me={me} embed={new URLSearchParams(window.location.search).get("embed") === "1"} />;
    case "prep":
    case "practice":
      // The operator's own pages, like Listen.
      if (me && me.handle.toLowerCase() !== ROOT_HANDLE) return <NothingHere />;
      if (!me) return <Loading />;
      return route.kind === "prep" ? <PrepScreen key={route.slug} slug={route.slug} me={me} /> : <PracticeScreen key={route.slug} slug={route.slug} handle={me.handle} />;
    case "unknown":
      return <NothingHere />;
    default:
      break;
  }

  // The owner opening their own room (their key names this handle) sees
  // "Your room"; everyone else sees whose call it is.
  const isOwnRoom = route.kind === "personal" && getKeyHandle() === route.slug.toLowerCase();
  const firstName = ownerName?.split(" ")[0] ?? null;

  if (joinedName === null) {
    return (
      <PreJoinScreen
        canRecord={isOwnRoom}
        waitingPreview={isOwnRoom ? { hostName: firstName, trivia: showsTrivia(route.kind, route.slug) } : undefined}
        onJoin={(name, record) => {
          setJoinMove((prev) => ({ move: false, n: prev.n + 1 }));
          setJoinRecord(record);
          setJoinedName(name);
        }}
        onMove={
          inCallElsewhere
            ? (name) => {
                setJoinMove((prev) => ({ move: true, n: prev.n + 1 }));
                setJoinedName(name);
              }
            : undefined
        }
      />
    );
  }

  return (
    <CallScreen
      key={joinMove.n}
      kind={route.kind}
      slug={route.slug}
      name={joinedName}
      hostName={firstName}
      move={joinMove.move}
      record={joinRecord}
      onLeft={() => {
        setJoinedName(null);
        setInCallElsewhere(false);
        leaveCallPage();
      }}
      onMoveHere={getUserKey() ? () => setJoinMove((prev) => ({ move: true, n: prev.n + 1 })) : undefined}
    />
  );
}

/** After Leave. Adam, 2026-10-04: "if i leave a "facetime" call ... i
 * shouldn't be taken to meet.am when i leave." A tab another app opened just
 * for the call closes, and a `return=` link goes back where it came from;
 * otherwise the caller stays on the room's pre-join page. */
function leaveCallPage(): void {
  const back = getReturnUrl();
  if (window.opener) {
    try {
      window.opener.postMessage({ type: "breeze:left" }, "*");
    } catch {
      // A cross-origin opener that went away.
    }
    window.close();
  }
  if (back) {
    // Still here when the tab could not close itself.
    window.setTimeout(() => window.location.assign(back), window.opener ? 150 : 0);
    return;
  }
}

function Loading(): JSX.Element {
  return (
    <div className="page">
      <div className="skeleton" style={{ height: 44, width: 160 }} />
      <div className="skeleton" style={{ height: 140 }} />
      <div className="skeleton" />
    </div>
  );
}
